import { Injectable } from '@angular/core';
import { Subject } from 'rxjs';

export interface UserNotificationActionLease {
  readonly leaseId: number;
  readonly workspaceId: string;
  readonly notificationId: string;
}

export interface UserNotificationPreferenceLease {
  readonly leaseId: number;
  readonly workspaceId: string;
}

@Injectable({ providedIn: 'root' })
export class UserNotificationCommandRegistry {
  private readonly actionLeases = new Map<
    string,
    UserNotificationActionLease
  >();
  private readonly preferenceLeases = new Map<
    string,
    UserNotificationPreferenceLease
  >();
  private readonly changesSubject = new Subject<void>();
  private nextLeaseId = 0;

  readonly changes$ = this.changesSubject.asObservable();

  acquireAction(
    workspaceId: string,
    notificationId: string,
  ): UserNotificationActionLease | null {
    const key = this.actionKey(workspaceId, notificationId);
    if (!key || this.actionLeases.has(key)) {
      return null;
    }

    const lease = {
      leaseId: this.nextLeaseId + 1,
      workspaceId: workspaceId.trim(),
      notificationId: notificationId.trim(),
    };
    this.nextLeaseId = lease.leaseId;
    this.actionLeases.set(key, lease);
    this.changesSubject.next();
    return lease;
  }

  releaseAction(lease: UserNotificationActionLease): boolean {
    const key = this.actionKey(lease.workspaceId, lease.notificationId);
    if (!key || this.actionLeases.get(key) !== lease) {
      return false;
    }

    this.actionLeases.delete(key);
    this.changesSubject.next();
    return true;
  }

  activeActionNotificationIds(workspaceId: string): readonly string[] {
    const normalizedWorkspaceId = workspaceId.trim();
    return Array.from(this.actionLeases.values())
      .filter((lease) => lease.workspaceId === normalizedWorkspaceId)
      .map((lease) => lease.notificationId);
  }

  acquirePreference(
    workspaceId: string,
  ): UserNotificationPreferenceLease | null {
    const normalizedWorkspaceId = workspaceId.trim();
    if (
      normalizedWorkspaceId.length === 0 ||
      this.preferenceLeases.has(normalizedWorkspaceId)
    ) {
      return null;
    }

    const lease = {
      leaseId: this.nextLeaseId + 1,
      workspaceId: normalizedWorkspaceId,
    };
    this.nextLeaseId = lease.leaseId;
    this.preferenceLeases.set(normalizedWorkspaceId, lease);
    this.changesSubject.next();
    return lease;
  }

  releasePreference(lease: UserNotificationPreferenceLease): boolean {
    if (this.preferenceLeases.get(lease.workspaceId) !== lease) {
      return false;
    }

    this.preferenceLeases.delete(lease.workspaceId);
    this.changesSubject.next();
    return true;
  }

  hasPreferenceLease(workspaceId: string): boolean {
    return this.preferenceLeases.has(workspaceId.trim());
  }

  private actionKey(
    workspaceId: string,
    notificationId: string,
  ): string | null {
    const normalizedWorkspaceId = workspaceId.trim();
    const normalizedNotificationId = notificationId.trim();
    return normalizedWorkspaceId.length > 0 &&
      normalizedNotificationId.length > 0
      ? `${normalizedWorkspaceId}\u0000${normalizedNotificationId}`
      : null;
  }
}
